/*
Designer Portfolio - Dark Theme with Grid Layout (TypeScript + React + Tailwind CSS)
Dark & Modern Designer Portfolio with Project Grid
*/

import React from "react";
import {
  Mail,
  ExternalLink,
  Figma,
  ArrowRight,
} from "lucide-react";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import { FiverrIcon, links } from "../data/Link";
import { projects } from "../data/Projects";
import { services } from "../data/Service";
import { FaWhatsapp } from "react-icons/fa";


const Home: React.FC = () => {
  return (
    <div className="min-h-screen text-white bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900">
      {/* Navigation */}
      <Navbar />

      <main className="pt-20">
        {/* Hero Section */}
        <section className="max-w-4xl px-6 py-20 mx-auto text-center">
          <h1 className="mb-6 text-4xl font-bold md:text-6xl">
            Creative Designer &
            <span className="text-transparent bg-gradient-to-r from-cyan-400 to-blue-500 bg-clip-text">
              {" "}
              Motion Visual Creator
            </span>
          </h1>
          <p className="max-w-2xl mx-auto mb-8 text-xl text-slate-300">
           I design elegant and meaningful animated invitation videos for weddings, birthdays, graduations, and other special moments. With Adobe After Effects and a focus on detail, I bring ideas to life through smooth motion, refined visuals, and cinematic storytelling. As both an animator and educator, I value creativity, precision, and purpose in every project I create.
          </p>

          <div className="flex flex-wrap justify-center gap-4">
            <a
              href="#work"
              className="px-6 py-3 text-white transition-all duration-300 rounded-lg bg-gradient-to-r from-cyan-500 to-blue-600 hover:from-cyan-600 hover:to-blue-700"
            >
              View My Work
            </a>
            <a
              href="#contact"
              className="px-6 py-3 transition-colors border rounded-lg border-slate-600 hover:border-cyan-500"
            >
              Get In Touch
            </a>
          </div>
        </section>

        {/* Projects Grid Section */}
        <section id="work" className="py-20 bg-slate-800/30">
          <div className="max-w-6xl px-6 mx-auto">
            <div className="mb-12 text-center">
              <h2 className="mb-4 text-3xl font-bold">Featured Projects</h2>
              <p className="max-w-2xl mx-auto text-slate-300">
                A selection of recent design works highlighting my creative approach to storytelling, event branding, and visual communication. Each project reflects thoughtful design decisions that connect purpose, emotion, and aesthetic harmony.
              </p>
            </div>

            {/* Projects Grid */}
            <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3">
              {projects.map((project) => (
                <div
                  key={project.id}
                  className="overflow-hidden transition-all duration-300 border bg-slate-800/50 rounded-xl backdrop-blur-sm border-slate-700 hover:border-cyan-500/50 group hover:transform hover:scale-105"
                >
                  {/* Project Image */}
                  <div className="relative overflow-hidden aspect-video bg-gradient-to-br from-slate-700 to-slate-800">
                    <div className="absolute inset-0 flex items-center justify-center text-slate-400">
                      <img  src={project.images[0]} className="object-cover w-96"/>
                    </div>

                    {/* Overlay on hover */}
                    <div className="absolute inset-0 flex items-center justify-center transition-opacity duration-300 opacity-0 bg-black/70 group-hover:opacity-100">
                      <a
                        href={project.link}
                        className="px-4 py-2 font-medium text-white transition-colors rounded-lg bg-cyan-500 hover:bg-cyan-600"
                      >
                        View Case Study
                      </a>
                    </div>
                  </div>

                  {/* Project Info */}
                  <div className="p-6">
                    <div className="flex items-start justify-between mb-3">
                      <div>
                        <span className="text-sm font-medium text-cyan-400">
                          {project.category}
                        </span>
                        <h3 className="mt-1 text-lg font-semibold transition-colors group-hover:text-cyan-400">
                          {project.title}
                        </h3>
                      </div>
                      <ExternalLink className="w-5 h-5 transition-colors text-slate-400 group-hover:text-cyan-400" />
                    </div>

                    <p className="mb-4 text-sm text-slate-300">
                      {project.desc.substring(0, 100)}...
                    </p>

                    <div className="flex flex-wrap gap-2">
                      {project.tags.map((tag) => (
                        <span
                          key={tag}
                          className="px-3 py-1 text-xs border rounded-full bg-slate-700/50 text-slate-300 border-slate-600"
                        >
                          {tag}
                        </span>
                      ))}
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* View All Projects Button */}
            <div className="mt-12 text-center">
              <a
                href="/projects"
                className="inline-flex items-center gap-2 px-6 py-3 transition-colors border rounded-lg border-slate-600 hover:border-cyan-500 hover:text-cyan-400"
              >
                View All Projects
                <ArrowRight className="w-4 h-4" />
              </a>
            </div>
          </div>
        </section>

        {/* Services Section */}
        <section id="services" className="py-20">
          <div className="max-w-6xl px-6 mx-auto">
            <div className="mb-12 text-center">
              <h2 className="mb-4 text-3xl font-bold">Design Services</h2>
              <p className="max-w-2xl mx-auto text-slate-300">
                Comprehensive design solutions crafted to communicate ideas beautifully and meaningfully.
              </p>
            </div>

            <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-4">
              {services.map(({ id, title, desc, icon: Icon }) => (
                <div
                  key={id}
                  className="p-6 text-center transition-all duration-300 border bg-slate-800/30 rounded-xl backdrop-blur-sm border-slate-700 hover:border-cyan-500/50 group"
                >
                  <div className="flex items-center justify-center w-12 h-12 mx-auto mb-4 transition-colors bg-cyan-500/20 rounded-xl group-hover:bg-cyan-500 group-hover:text-white">
                    <Icon />
                  </div>
                  <h3 className="mb-3 text-lg font-semibold">{title}</h3>
                  <p className="text-sm text-slate-300">{desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* About Section */}
        <section id="about" className="py-20 bg-slate-800/30">
          <div className="max-w-6xl px-6 mx-auto">
            <div className="grid items-center gap-12 md:grid-cols-2">
              <div>
                <h2 className="mb-6 text-3xl font-bold">About Me</h2>
                <p className="mb-4 text-slate-300">
                  I'm a professional animator specializing in high-quality animated invitation videos
                  for weddings, birthdays, graduations, and more.

               </p>
                <p className="mb-6 text-slate-300">
                 I use professional software like Adobe After Effects to create elegant and visually
                 appealing animations with smooth motion, refined details, and a polished final look.
                 Every project is carefully designed to match your vision with precision and creativity.
 
                </p>

                <div className="flex gap-4">
                  {links.map(({ label, icon, href }) => (
                    <a
                      key={label}
                      href={href}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center justify-center w-10 h-10 transition-colors border rounded-lg bg-slate-700/50 border-slate-600 hover:border-cyan-500 hover:text-cyan-400"
                      title={label}
                    >
                      {icon}
                    </a>
                  ))}
                </div>
              </div>

              <div className="flex items-center justify-center bg-white border bg-slate-700/50 rounded-2xl aspect-square border-slate-600">
            

                  <img src="/logo.png" className="object-cover" />

              </div>
            </div>
          </div>
        </section>

        {/* Contact Section */}
        <section id="contact" className="py-20">
          <div className="max-w-4xl px-6 mx-auto text-center">
            <h2 className="mb-6 text-3xl font-bold">Let's Work Together</h2>
            <p className="max-w-2xl mx-auto mb-8 text-slate-300">
              Have a story to tell or an event to design? I’d love to collaborate and bring your vision to life through thoughtful, meaningful design.
            </p>

            <div className="flex flex-wrap justify-center gap-4">
              <a
                target="_blank"
                href="https://www.fiverr.com/silvintage"
                className="inline-flex items-center gap-2 px-6 py-3 text-white transition-all duration-300 bg-green-600 rounded-lg hover:from-cyan-600 hover:to-blue-700"
              >
                <FiverrIcon />
                Fiverr
              </a>
               <a
               target="_blank"
                href="https://api.whatsapp.com/send?phone=6283818181806&text=Hay%2C%20I%20wanna%20order%20this%20design%20!"
                className="inline-flex items-center gap-2 px-6 py-3 text-white transition-all duration-300 bg-green-700 rounded-lg hover:from-cyan-600 hover:to-blue-700"
              >
                <FaWhatsapp className="w-5 h-5" />
                Whatsapp
              </a>
              {/* <a
                href="#"
                className="px-6 py-3 transition-colors border rounded-lg border-slate-600 hover:border-cyan-500"
              >
                Download Resume
              </a> */}
            </div>
          </div>
        </section>
      </main>

      {/* Footer */}
      <Footer />
    </div>
  );
};

export default Home;
